import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";

import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Button } from "@mapmycustomers/ui";

import badgesDisabled from "@app/assets/map/legends/badges-disabled.svg";
import ButtonLink from "@app/component/ButtonLink";
import FrequencyInfo from "@app/component/frequency/FrequencyPanel/component/FrequencyInfo";
import useMapView from "@app/component/view/utils/useMapView";
import AlertBadgesType, {
  getAlertBadgesTypeDescription,
  getAlertBadgesTypeName,
} from "@app/enum/map/AlertBadgesType";
import { enableEngagementFields, toggleAlertBadgeVisibility } from "@app/scene/map/store/actions";
import { setAlertDismissed, toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import { getAlertDismissed, getVisibleBadges } from "@app/scene/map/store/selectors";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import getBadgeImage from "@app/util/badge/getBadgeImage";
import { getBadgeSvgWithColor } from "@app/util/badge/getSvgForBadge";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getColorByBadgeType from "@app/util/map/markerStyles/getColorByBadgeType";

import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import styles from "./AlertBadgesLegendCard.module.scss";
import EngagementInformationPopover from "./EngagementInformationPopover";

interface Props extends BaseLegendCardProps {
  engagement: boolean;
  isAlertDismissed: boolean;
  onEnableEngagementFields: typeof enableEngagementFields.request;
  setAlertDismissed: typeof setAlertDismissed;
  toggleAlertBadgeVisibility: typeof toggleAlertBadgeVisibility;
  toggleLayerVisibility: typeof toggleLayerVisibility;
  visibleBadges: AlertBadgesType[];
}

const AlertBadgesLegendCard: React.FC<Props> = ({
  engagement,
  isAlertDismissed,
  onEnableEngagementFields,
  setAlertDismissed,
  toggleAlertBadgeVisibility,
  toggleLayerVisibility,
  visible,
  visibleBadges,
  ...props
}) => {
  const [hoveredType, setHoveredType] = useState<AlertBadgesType | undefined>();
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Alert Badges Key", useAnalytics());
  const {
    viewState: { visibleEntities },
  } = useMapView();

  const [isAlertVisible, setAlertVisibility] = useState(false);

  useEffect(() => setAlertVisibility(!engagement), [visibleEntities, engagement]);

  const handleToggleVisibility = useCallback(
    (type: AlertBadgesType) => {
      const typeName =
        type === AlertBadgesType.OVERDUE
          ? "Overdue Activity or Out of Cadence"
          : type === AlertBadgesType.OUT_OF_CADENCE_SOON
          ? "Cadence Upcoming"
          : "Next Activity";
      analytics.clicked([typeName, visibleBadges.includes(type) ? "Hide" : "Show"]);
      toggleAlertBadgeVisibility(type);
    },
    [analytics, toggleAlertBadgeVisibility, visibleBadges]
  );

  const handleToggle = useCallback(
    (visible) => {
      analytics.clicked([visible ? "On" : "Off"]);
      toggleLayerVisibility({ name: "alerts", visible });
    },
    [analytics, toggleLayerVisibility]
  );

  const handleDismissClick = useCallback(() => {
    setAlertDismissed({ alertDismissed: true });
  }, [setAlertDismissed]);

  useChangeTracking(() => {
    if (props.active) {
      analytics.clicked([]);
    }
  }, [analytics, props.active]);

  return (
    <LegendCard
      {...props}
      icon={<FontAwesomeIcon icon={faExclamationCircle} />}
      onVisibilityChange={handleToggle}
      title={intl.formatMessage({
        id: "map.layer.alertBadges.legend",
        defaultMessage: "Alert Badges",
        description: "Title of the Shape Key legend",
      })}
      visible={visible}
    >
      {visible ? (
        <div className={styles.container}>
          <div className={styles.settingsWrapper}>
            {[
              AlertBadgesType.OVERDUE,
              AlertBadgesType.OUT_OF_CADENCE_SOON,
              AlertBadgesType.UPCOMING_ACTIVITY,
            ].map((type) => (
              <div
                className={cn(styles.alertContainer, styles[`alertContainer__${type}`])}
                key={type}
              >
                <Row
                  align="middle"
                  className={styles.row}
                  justify="space-between"
                  onMouseEnter={() => setHoveredType(type)}
                  onMouseLeave={() => setHoveredType(undefined)}
                >
                  <Col span={1}>
                    {getBadgeImage(getBadgeSvgWithColor(getColorByBadgeType(type)))}
                  </Col>
                  <Col className={styles.alertBadgeText} span={19}>
                    {getAlertBadgesTypeName(intl, type)}
                  </Col>
                  <Col span={2}>
                    <div className={styles.hideIconWrapper}></div>
                    {hoveredType === type && (
                      <FontAwesomeIcon
                        className={styles.hideIcon}
                        icon={visibleBadges.includes(type) ? faEye : faEyeSlash}
                        onClick={() => handleToggleVisibility(type)}
                      />
                    )}
                  </Col>
                </Row>
                <Row align="middle" justify="space-between">
                  <Col span={1} />
                  <Col className={styles.alertBadgeDescription} span={19}>
                    {getAlertBadgesTypeDescription(intl, type)}
                  </Col>
                  <Col span={2} />
                </Row>
                {type === AlertBadgesType.OVERDUE && (
                  <Row align="middle" className={styles.overdueInfo} justify="space-between">
                    <Col span={1} />
                    <Col span={19}>
                      <FrequencyInfo />
                    </Col>
                    <Col span={2} />
                  </Row>
                )}
              </div>
            ))}
          </div>
          {isAlertVisible && !isAlertDismissed && (
            <div className={styles.info}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Row align="middle" gutter={layout.spacerS}>
                    <Col>
                      <EngagementInformationPopover />
                    </Col>
                    <Col className={styles.engagementInfoTitle}>
                      {intl.formatMessage({
                        id: "map.layer.alertBadges.popover.disabled.engagement.alerts.title",
                        defaultMessage: "Engagement info disabled on previews",
                        description: "Alert badges disabled - description",
                      })}
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <FontAwesomeIcon icon={faTimes} onClick={handleDismissClick} />
                </Col>
              </Row>

              <div className={styles.engagementInfoSubtitle}>
                {intl.formatMessage({
                  id: "map.layer.alertBadges.popover.disabled.engagement.alerts.info",
                  defaultMessage:
                    "This information is disabled on at least one visible record type. We recommend showing these details when viewing alert badges on the map.",
                  description: "Alert badges disabled - description",
                })}
              </div>

              <div className={styles.buttons}>
                <Button onClick={() => onEnableEngagementFields()} size="small" type="primary">
                  {intl.formatMessage({
                    id: "map.layer.alertBadges.description.enable.button",
                    defaultMessage: "Enable",
                    description: "Enable button for engangement info",
                  })}
                </Button>
                <Button onClick={handleDismissClick} size="small">
                  {intl.formatMessage({
                    id: "map.layer.alertBadges.description.dismiss.button",
                    defaultMessage: "Dismiss",
                    description: "Dismiss button for engangement info",
                  })}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.disabledContainer}>
          <img alt="badges-disabled" className={styles.disabledImage} src={badgesDisabled} />
          <div className={styles.disabledDescription}>
            {intl.formatMessage({
              id: "map.layer.alertBadges.disabledDescription",
              defaultMessage: "Get alerted when records need attention",
              description: "Alert badges legend - disabled description",
            })}
          </div>
          <ButtonLink onClick={() => handleToggle(true)}>
            {intl.formatMessage({
              id: "map.layer.alertBadges.disabledDescription.enable",
              defaultMessage: "Enable Alert Badges",
              description: "Alert badges legend - Enable Alert Badges",
            })}
          </ButtonLink>
        </div>
      )}
    </LegendCard>
  );
};

const mapStateToProps = (state: RootState) => {
  const { engagement } = getRecordPreviewConfigurationColumns(state);
  return {
    engagement,
    isAlertDismissed: getAlertDismissed(state),
    visibleBadges: getVisibleBadges(state),
  };
};

const mapDispatchToProps = {
  onEnableEngagementFields: enableEngagementFields.request,
  setAlertDismissed,
  toggleAlertBadgeVisibility,
  toggleLayerVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertBadgesLegendCard);
